/**
 * @file React Query hooks for the Xoe AI assistant feature.
 *
 * All hooks are flat exports from this single file (matching the src/hooks/
 * convention). They are shared across the Xoe chat UI components (S10/S11/S12).
 *
 * Query-key shape:
 *   conversations list  → ['xoeConversations', patientId?, searchTerm?]
 *   single conversation → ['xoeConversation', uuid]
 *   experts list        → ['xoeExperts']
 */

import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient, QueryClient } from '@tanstack/react-query';

import {
	listXoeConversations,
	createXoeConversation,
	getXoeConversation,
	updateXoeConversation,
	removeXoeConversation,
	resolveXoePatient,
	CreateConversationPayload,
	UpdateConversationPayload,
	ResolveXoePatientParams,
} from '../../api/xoe/XoeConversationsApi';
import { listXoeExperts } from '../../api/xoe/XoeExpertsApi';
import { listXoeTools } from '../../api/xoe/XoeToolsApi';
import {
	confirmXoeAction,
	ConfirmXoeActionPayload,
	recordXoeOpenAction,
	RecordOpenActionPayload,
	dismissXoeAction,
	DismissXoeActionPayload,
} from '../../api/xoe/XoeActionsApi';

import {
	XoeConversation,
	XoeConversationDetail,
	XoeExpert,
	XoeMessage,
	XoePatientCandidate,
	XoeProposedAction,
	XoeTool,
} from '../../types/xoe/XoeTypes';
import type {
	XoeTurnCompletedEvent,
	XoeTurnErrorEvent,
} from '../../types/xoe/XoeStreamTypes';
import type { UserUuid } from '../../types/identity';

// ── Conversations list ────────────────────────────────────────────────────────

/**
 * List Xoe conversations for the practitioner, optionally scoped to a patient and/or
 * narrowed by a free-text search (X6).
 */
export const useXoeConversations = (patientId?: UserUuid, search?: string, enabled: boolean = true) => {
	const q = search?.trim() || undefined;

	return useQuery<XoeConversation[]>({
		queryKey: ['xoeConversations', patientId ?? null, q ?? null],
		queryFn: () => listXoeConversations({ patient_id: patientId, q }),
		enabled,
	});
};

// ── Single conversation ───────────────────────────────────────────────────────

/**
 * Fetch a conversation with its message history (one-shot loader for when a
 * conversation is opened). There is no polling: the turn streams, and
 * `useStreamXoeMessage` writes the terminal frame straight into this query's cache
 * rather than triggering a refetch.
 */
export const useXoeConversation = (uuid: string) => {
	return useQuery<XoeConversationDetail>({
		queryKey: ['xoeConversation', uuid],
		queryFn: () => getXoeConversation({ uuid }),
		enabled: !!uuid,
	});
};

// ── Experts ───────────────────────────────────────────────────────────────────

/**
 * List available Xoe expert personas for the clinic.
 *
 * queryKey: ['xoeExperts']
 */
export const useXoeExperts = () => {
	return useQuery<XoeExpert[]>({
		queryKey: ['xoeExperts'],
		queryFn: () => listXoeExperts(),
		enabled: true,
	});
};

// ── Available tools (command discovery) ────────────────────────────────────────

/**
 * List the tool calls available for a conversation's resolved expert, for the
 * composer's command-discovery dropdown.
 *
 * queryKey: ['xoeTools', uuid]
 */
export const useXoeTools = (uuid: string) => {
	return useQuery<XoeTool[]>({
		queryKey: ['xoeTools', uuid],
		queryFn: () => listXoeTools({ uuid }),
		enabled: !!uuid,
	});
};

// ── Create conversation ───────────────────────────────────────────────────────

/**
 * Create a new Xoe conversation.
 * On success invalidates the conversations list.
 */
export const useCreateXoeConversation = () => {
	const queryClient = useQueryClient();

	return useMutation<XoeConversation, Error, CreateConversationPayload>({
		mutationFn: (vars) => createXoeConversation(vars),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['xoeConversations'] });
		},
	});
};

// ── Send message (STREAMING) ──────────────────────────────────────────────────

/**
 * Send a user message over the SSE stream and write the terminal frame into the cache.
 */
export const useStreamXoeMessage = () => {
	const queryClient = useQueryClient();

	/**
	 * Append the completed turn (user echo + assistant reply) to the cached thread.
	 */
	const applyCompletedTurn = useCallback((
		uuid: string,
		userText: string,
		frame: XoeTurnCompletedEvent,
	) => {
		const existing = queryClient.getQueryData<XoeConversationDetail>([
			'xoeConversation',
			uuid,
		]);

		// S10: a turn that left memory proposals behind refreshes an open Memory tab.
		if ((frame.pending_memory_proposals ?? 0) > 0) {
			queryClient.invalidateQueries({ queryKey: ['xoe', 'memories'] });
		}

		// The persisted identity. Any one of these missing means the turn did not reach
		// the database, so there is nothing honest to write — refetch instead.
		const messageId = frame.message_id;
		const userMessageId = frame.user_message_id;
		const createdAt = frame.created_at;

		if (
			!existing ||
			messageId == null ||
			userMessageId == null ||
			createdAt == null
		) {
			// Nothing resident to append to, or no persisted row to append — refetch
			// instead of inventing an envelope or an id.
			queryClient.invalidateQueries({ queryKey: ['xoeConversation', uuid] });
			queryClient.invalidateQueries({ queryKey: ['xoeConversations'] });
			return;
		}

		const conversationId = existing.conversation.id;

		const userMessage: XoeMessage = {
			id: userMessageId,
			conversation_id: conversationId,
			role: 'user',
			content: userText,
			content_blocks: null,
			tool_calls: null,
			persona: null,
			status: 'complete',
			created_at: createdAt,
		};

		const assistantMessage: XoeMessage = {
			id: messageId,
			conversation_id: conversationId,
			role: 'assistant',
			content: frame.text,
			content_blocks: frame.content_blocks ?? null,
			// The proposals arrive in the shape XoeActionCardList already consumes —
			// server-stamped id/kind/status — so the action cards need no change.
			tool_calls: frame.proposed_actions ?? null,
			persona: frame.persona || null,
			// P7.1. Taken from the TERMINAL frame, not accumulated from the live
			// `reasoning.block` frames — those ride the bounded progress queue and are
			// dropped under pressure, so accumulating them here would write a cache entry
			// that silently disagrees with the stored row. Empty list => null, matching
			// what the column holds when ANTHROPIC_THINKING is off.
			reasoning: frame.reasoning?.length ? frame.reasoning : null,
			status: frame.status || 'complete',
			created_at: createdAt,
		};

		queryClient.setQueryData<XoeConversationDetail>(['xoeConversation', uuid], {
			...existing,
			messages: {
				...existing.messages,
				data: [...existing.messages.data, userMessage, assistantMessage],
				total: existing.messages.total + 2,
			},
		});

		// The thread came from the stream; only the list needs refreshing.
		queryClient.invalidateQueries({ queryKey: ['xoeConversations'] });
	}, [queryClient]);

	/** Write a failed turn as a visibly-errored assistant message (never silence). */
	const applyErrorTurn = useCallback((uuid: string, userText: string, frame: XoeTurnErrorEvent) => {
		applyCompletedTurn(uuid, userText, {
			event: 'turn.completed',
			v: frame.v,
			// `status: 'error'` is what makes MessageBubble render it distinctly. The
			// message text is the server's STATIC safe string, never an exception.
			status: 'error',
			text: frame.message,
			content_blocks: [],
			proposed_actions: [],
			persona: frame.persona,
			iterations: 0,
			tool_calls: 0,
			capped: false,
			// An error turn still persists an assistant row (`status='error'`, static
			// text), so its ids are forwarded rather than dropped — otherwise exactly
			// the bubble a practitioner is most likely to want to reference would be
			// the one without an id.
			message_id: frame.message_id,
			user_message_id: frame.user_message_id,
			created_at: frame.created_at,
		});
	}, [applyCompletedTurn]);

	return { applyCompletedTurn, applyErrorTurn };
};

// ── Update conversation ───────────────────────────────────────────────────────

/**
 * Update a conversation's title or archived state.
 * On success invalidates both the single-conversation and list queries.
 */
export const useUpdateXoeConversation = () => {
	const queryClient = useQueryClient();

	return useMutation<XoeConversation, Error, UpdateConversationPayload>({
		mutationFn: (vars) => updateXoeConversation(vars),
		onSuccess: (_data, vars) => {
			queryClient.invalidateQueries({
				queryKey: ['xoeConversation', vars.uuid],
			});
			queryClient.invalidateQueries({ queryKey: ['xoeConversations'] });
		},
	});
};

// ── Resolve patient (by-name binding) ───────────────────────────────────────────

export const useResolveXoePatient = () => {
	return useMutation<
		XoePatientCandidate[],
		Error,
		ResolveXoePatientParams
	>({
		mutationFn: (vars) => resolveXoePatient(vars),
	});
};

// ── Delete conversation ───────────────────────────────────────────────────────

/**
 * Delete a conversation.
 * On success invalidates the conversations list.
 */
export const useDeleteXoeConversation = () => {
	const queryClient = useQueryClient();

	return useMutation<void, Error, { uuid: string }>({
		mutationFn: (vars) => removeXoeConversation(vars),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['xoeConversations'] });
		},
	});
};

// ── Confirm action ────────────────────────────────────────────────────────────

/**
 * Merge an outcome into one `tool_calls` entry of the cached thread so the card flips on the
 * response itself; the server-side stamp makes the refetch agree. Without a uuid the whole
 * `['xoeConversation']` family is scanned — message ids are global, so the match is exact either way.
 */
const stampCachedAction = (
	queryClient: QueryClient,
	conversationUuid: string | undefined,
	messageId: number,
	actionId: string,
	patch: Partial<XoeProposedAction>,
) => {
	queryClient.setQueriesData<XoeConversationDetail>(
		{ queryKey: conversationUuid ? ['xoeConversation', conversationUuid] : ['xoeConversation'] },
		(existing) => {
			if (!existing) return existing;
			let touched = false;
			const data = existing.messages.data.map((message) => {
				if (message.id !== messageId || !message.tool_calls?.some((call) => call.id === actionId)) {
					return message;
				}
				touched = true;
				return {
					...message,
					tool_calls: message.tool_calls.map((call) => (call.id === actionId ? { ...call, ...patch } : call)),
				};
			});
			return touched ? { ...existing, messages: { ...existing.messages, data } } : existing;
		},
	);
};

/**
 * Confirm a proposed assistant action.
 * On success patches the cached thread with the response's linkage (the card flips at once),
 * then invalidates the conversation (the refetch carries the server-side stamp) and the list.
 *
 * Pass `conversationUuid` in the mutation vars alongside messageId / actionId / draft.
 */
export const useConfirmXoeAction = () => {
	const queryClient = useQueryClient();

	return useMutation<
		Awaited<ReturnType<typeof confirmXoeAction>>,
		Error,
		ConfirmXoeActionPayload
	>({
		mutationFn: (vars) => confirmXoeAction(vars),
		onSuccess: (data, vars) => {
			stampCachedAction(queryClient, vars.conversationUuid, vars.messageId, vars.actionId, {
				status: 'confirmed',
				linked_item_type: data.linked_item_type ?? undefined,
				linked_item_id: data.linked_item_id ?? undefined,
				confirmed_at: data.confirmed_at ?? undefined,
			});
			queryClient.invalidateQueries({
				queryKey: ['xoeConversation', vars.conversationUuid],
			});
			queryClient.invalidateQueries({ queryKey: ['xoeConversations'] });
		},
	});
};

// ── Record open action (audit) ──────────────────────────────────────────────────

/**
 * Record that an open/navigation action was executed (audit only — no record created).
 * Fired best-effort when the practitioner clicks "Open"; errors are swallowed by the
 * caller so the audit can never block the navigation (defect 1C).
 */
export const useRecordXoeOpenAction = () => {
	return useMutation<
		Awaited<ReturnType<typeof recordXoeOpenAction>>,
		Error,
		RecordOpenActionPayload
	>({
		mutationFn: (vars) => recordXoeOpenAction(vars),
	});
};

// ── Dismiss action (audit) ──────────────────────────────────────────────────────

/**
 * Dismiss (reject) a proposed assistant action.
 */
export const useDismissXoeAction = () => {
	const queryClient = useQueryClient();

	return useMutation<
		Awaited<ReturnType<typeof dismissXoeAction>>,
		Error,
		DismissXoeActionPayload
	>({
		mutationFn: (vars) => dismissXoeAction(vars),
		onSuccess: (data, vars) => {
			stampCachedAction(queryClient, vars.conversationUuid, vars.messageId, vars.actionId, {
				status: 'dismissed',
				dismissed_at: data.dismissed_at ?? undefined,
			});
		},
	});
};
